import React, { useState, useRef, useEffect } from 'react';
import { useAnnouncements } from '../../contexts/AnnouncementsContext';
import { ChevronLeftIcon, ChevronRightIcon, BellIcon } from '@heroicons/react/24/outline';
import './AnnouncementsCard.css';

const AnnouncementsCard = ({
  maxItems = 3,
  showUrgentOnly = false,
  showWishlistDeadlines = true
}) => {
  const {
    announcements,
    wishlistDeadlines,
    loading,
    getUrgentAnnouncements
  } = useAnnouncements();

  // Mobile navigation state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef(null);

  // Get announcements to display
  const getDisplayAnnouncements = () => {
    let displayAnnouncements = [];

    if (showUrgentOnly) {
      displayAnnouncements = getUrgentAnnouncements();
    } else {
      // Use all announcements and wishlist deadlines
      const allAnnouncements = announcements || [];
      const deadlines = showWishlistDeadlines ? wishlistDeadlines : [];

      // Merge and deduplicate
      const combined = [...allAnnouncements, ...deadlines];
      const uniqueAnnouncements = combined.filter((ann, index, self) =>
        index === self.findIndex(a => a.id === ann.id)
      );

      // Sort by priority and deadline urgency
      displayAnnouncements = uniqueAnnouncements.sort((a, b) => {
        const priorityOrder = { 'Urgent': 4, 'High': 3, 'Normal': 2, 'Low': 1 };
        const aPriority = priorityOrder[a.priority_display] || 0;
        const bPriority = priorityOrder[b.priority_display] || 0;

        if (aPriority !== bPriority) {
          return bPriority - aPriority;
        }

        // If same priority, prioritize deadline announcements
        if (a.is_deadline_approaching && !b.is_deadline_approaching) return -1;
        if (!a.is_deadline_approaching && b.is_deadline_approaching) return 1;

        return 0;
      });
    }

    return displayAnnouncements.slice(0, maxItems);
  };

  const displayAnnouncements = getDisplayAnnouncements();

  // Scroll management functions
  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollToIndex = (index) => {
    if (scrollContainerRef.current && displayAnnouncements[index]) {
      const itemWidth = 296; // 280px + 16px gap
      const scrollPosition = index * itemWidth;
      scrollContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };

  const scrollLeft = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
  };

  const scrollRight = () => {
    const newIndex = Math.min(displayAnnouncements.length - 1, currentIndex + 1);
    scrollToIndex(newIndex);
  };

  // Update scroll buttons when announcements change
  useEffect(() => {
    updateScrollButtons();
  }, [displayAnnouncements]);

  // Don't render if no announcements or loading
  if (loading) {
    return (
      <div className="announcements-loading">
        <div className="loading-spinner"></div>
        <span style={{ color: '#6B7280', fontSize: '14px', marginLeft: '8px' }}>Loading announcements...</span>
      </div>
    );
  }

  if (displayAnnouncements.length === 0) {
    return (
      <div className="announcements-empty">
        <BellIcon style={{ width: '24px', height: '24px', color: '#9CA3AF', marginRight: '8px' }} />
        <span style={{ color: '#6B7280', fontSize: '14px' }}>No announcements at this time</span>
      </div>
    );
  }

  return (
    <div className="announcements-scroll-container">
      {/* Navigation Arrows for Desktop */}
      {displayAnnouncements.length > 1 && (
        <>
          <button
            className={`scroll-arrow scroll-arrow-left ${!canScrollLeft ? 'disabled' : ''}`}
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            aria-label="Previous announcement"
          >
            <ChevronLeftIcon className="arrow-icon" />
          </button>
          <button
            className={`scroll-arrow scroll-arrow-right ${!canScrollRight ? 'disabled' : ''}`}
            onClick={scrollRight}
            disabled={!canScrollRight}
            aria-label="Next announcement"
          >
            <ChevronRightIcon className="arrow-icon" />
          </button>
        </>
      )}

      <div
        className="announcements-scroll"
        ref={scrollContainerRef}
        onScroll={updateScrollButtons}
      >
        {displayAnnouncements.map((announcement, index) => (
          <div
            key={announcement.id}
            className={`announcement-item ${index === currentIndex ? 'active' : ''}`}
          >
            <div className="announcement-header">
              <div className="announcement-icon">
                {announcement.is_urgent ? '🚨' :
                 announcement.announcement_type === 'maintenance' ? '🔧' :
                 announcement.announcement_type === 'new_course' ? '🎓' :
                 announcement.announcement_type === 'deadline' ? '⏰' : '📢'}
              </div>
              <div className="announcement-content">
                <h4 className="announcement-title">{announcement.title}</h4>
                <p className="announcement-text">{announcement.content}</p>
                <div className="announcement-meta">
                  <span className={`priority-badge ${(announcement.priority_display || 'normal').toLowerCase()}`}>
                    {announcement.priority_display || 'Normal'}
                  </span>
                  {announcement.time_until_deadline && (
                    <span className="deadline-badge">
                      ⏰ {announcement.time_until_deadline} remaining
                    </span>
                  )}
                  {announcement.is_deadline_approaching && (
                    <span className="urgent-deadline-badge">
                      🔥 Deadline Soon
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile-Friendly Dot Indicators */}
      {displayAnnouncements.length > 1 && (
        <div className="scroll-indicators">
          {displayAnnouncements.map((_, index) => (
            <button
              key={index}
              className={`scroll-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => scrollToIndex(index)}
              aria-label={`Go to announcement ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Announcement Counter for Mobile */}
      {displayAnnouncements.length > 1 && (
        <div className="announcement-counter">
          {currentIndex + 1} of {displayAnnouncements.length}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsCard;
